import React, { useEffect, useMemo, useState } from 'react';
import useBundleTranslation from 'i18n';
import { Box, Stack, Typography, FormHelperText } from '@mui/material';
import AiInsightsList from './AiInsightsList';
import { FormComponentValue } from 'components/common/form/layout/control';
import { TemplateFormComponent } from 'components/common/form/layout/ReactHookFormController';
import WrapperFormLabel from 'components/common/form/layout/helper/WrapperFormLabel';
import { FormDataAPIType } from 'components/common/form';
import { IAiInsight } from './index';

type aiInclusionType = 'all' | 'selected' | 'none';
export default function AiInsightsForm({
    elementProps,
    aiInsights,
}: {
    elementProps: FormDataAPIType;
    aiInsights: IAiInsight[];
}) {
    const { t } = useBundleTranslation(['components/notification/burst']);
    const values: FormComponentValue[] = useMemo(
        () =>
            aiInsights.map((v) => ({
                value: String(v.id),
                label: v.title,
                props: {
                    desc: v.desc,
                    isConditional: v.isConditional,
                },
            })),
        [aiInsights],
    );
    const inclusion: aiInclusionType = elementProps.form.hookFormWatch('ai_insight_inclusion_mode') ?? 'none';

    const [includedSet, setIncludedSet] = useState<string[]>(
        elementProps.form.hookFormGetValues('ai_included_set')?.map((v: number | string) => String(v)) ?? [],
    );

    useEffect(() => {
        elementProps.form.hookFormSetValue('ai_included_set', includedSet);
    }, [includedSet]);

    const effectiveValues = useMemo(
        () => (inclusion === 'all' ? values : values.filter((i) => includedSet.includes(i.value))),
        [values, inclusion, includedSet],
    );

    const showConditionalGate = useMemo(
        () => inclusion != 'none' && effectiveValues.length > 0 && effectiveValues.every((i) => i.props?.isConditional),
        [inclusion, effectiveValues],
    );

    const conditionalCount = useMemo(
        () => effectiveValues.filter((i) => i.props?.isConditional).length,
        [effectiveValues],
    );

    const handleToggle = (value: string, checked: boolean) => {
        setIncludedSet((prev) => (checked ? [...prev, value] : prev.filter((x) => x !== value)));
    };

    return (
        <Stack spacing={2}>
            <Typography variant="heading" className="heading-item" sx={{ pt: 2 }}>
                <Box component="span" className="heading-item__label">
                    {t('ai_insights.block_title')}
                </Box>
            </Typography>
            <Stack>
                <WrapperFormLabel>{t('ai_insights.include_in_burst')}</WrapperFormLabel>
                <TemplateFormComponent
                    elementProps={elementProps}
                    component={'FormToggleButtonGroup'}
                    componentValues={[
                        { label: t('ai_insights.all_insights'), value: 'all', disabled: aiInsights.length == 0 },
                        {
                            label: t('ai_insights.selected_insights'),
                            value: 'selected',
                            disabled: aiInsights.length == 0,
                        },
                        { label: t('ai_insights.none_insights'), value: 'none' },
                    ]}
                    name={'ai_insight_inclusion_mode'}
                />
                <FormHelperText sx={{ mx: 0 }}>
                    {t(aiInsights.length == 0 ? 'ai_insights.helper_no_insights' : `ai_insights.helper_${inclusion}`)}
                </FormHelperText>
            </Stack>

            {inclusion != 'none' && (
                <>
                    <Stack spacing={2}>
                        <Typography variant="heading" className="heading-item" sx={{ pt: 2 }}>
                            <Box component="span" className="heading-item__label">
                                {t('ai_insights.grid_title')}
                            </Box>
                        </Typography>
                        <AiInsightsList
                            values={values}
                            mode={inclusion}
                            checkedSet={includedSet}
                            onToggle={handleToggle}
                            conditionalChipLabel={t('ai_insights.conditional')}
                        />
                    </Stack>
                    {showConditionalGate && (
                        <Box>
                            <TemplateFormComponent
                                elementProps={elementProps}
                                component={'FormCheckbox'}
                                name={'ai_insight_conditional_gate_ind'}
                                label={
                                    conditionalCount === 1
                                        ? t('ai_insights.conditional_gate_one')
                                        : t('ai_insights.conditional_gate_many')
                                }
                            />
                        </Box>
                    )}
                </>
            )}
        </Stack>
    );
}
